// src/admin/components/reports/PaymentLogsReport.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine,
  RiCalendarLine,
  RiFileChartLine,
  RiAlertLine,
  RiDownloadLine,
  RiRefreshLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiFilter2Line,
  RiSaveLine,
  RiPrinterLine,
  RiSearchLine,
  RiCloseLine,
  RiUserLine,
  RiBuildingLine
} from 'react-icons/ri';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  getPaymentLogs,
  formatCurrency,
  formatDate,
  TIME_PERIOD_OPTIONS,
  getCurrentMonthStartEnd,
  getCurrentYearStartEnd,
  getCurrentQuarterStartEnd,
  getMonthStartEnd
} from '../../../models/reportModel';
// Remove the exportLogsToExcel import since it doesn't exist
import * as XLSX from 'xlsx';

const PaymentLogsReport = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const printRef = useRef(null);

  // Check if the current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // Select the appropriate arrow icon based on RTL
  const BackArrow = isRTL ? RiArrowRightLine : RiArrowLeftLine;

  // Get params from URL
  const defaultDates = getCurrentMonthStartEnd();
  const startDate = searchParams.get('startDate') || defaultDates.startDate;
  const endDate = searchParams.get('endDate') || defaultDates.endDate;
  const paymentType = searchParams.get('paymentType') || '';
  const buildingId = searchParams.get('buildingId') || '';
  const ownerId = searchParams.get('ownerId') || '';
  const tenantId = searchParams.get('tenantId') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const limit = parseInt(searchParams.get('limit') || '20');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [filters, setFilters] = useState({
    startDate,
    endDate,
    paymentType,
    buildingId,
    ownerId,
    tenantId,
    page,
    limit
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Set the initial period based on the URL params
    const detectPeriod = () => {
      const currentMonth = getCurrentMonthStartEnd();
      const lastMonth = getMonthStartEnd(
        new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
        new Date().getMonth() === 0 ? 12 : new Date().getMonth()
      );
      const currentQuarter = getCurrentQuarterStartEnd();
      const currentYear = getCurrentYearStartEnd();

      if (startDate === currentMonth.startDate && endDate === currentMonth.endDate) {
        return 'current_month';
      } else if (startDate === lastMonth.startDate && endDate === lastMonth.endDate) {
        return 'last_month';
      } else if (startDate === currentQuarter.startDate && endDate === currentQuarter.endDate) {
        return 'current_quarter';
      } else if (startDate === currentYear.startDate && endDate === currentYear.endDate) {
        return 'current_year';
      } else {
        return 'custom';
      }
    };

    setSelectedPeriod(detectPeriod());
  }, [startDate, endDate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const offset = (page - 1) * limit;

        const apiFilters = {
          startDate,
          endDate,
          limit,
          offset
        };

        if (paymentType) apiFilters.paymentType = paymentType;
        if (buildingId) apiFilters.buildingId = buildingId;
        if (ownerId) apiFilters.ownerId = ownerId;
        if (tenantId) apiFilters.tenantId = tenantId;

        const response = await getPaymentLogs(apiFilters);
        setData(response);
      } catch (err) {
        console.error('Error fetching payment logs:', err);
        setError(err.message || t('failed_to_load_payment_logs') || 'Failed to load payment logs');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, paymentType, buildingId, ownerId, tenantId, page, limit, t]);

  const handlePeriodChange = (e) => {
    const period = e.target.value;
    setSelectedPeriod(period);

    let newDates = {};

    switch (period) {
      case 'current_month':
        newDates = getCurrentMonthStartEnd();
        break;
      case 'last_month':
        const now = new Date();
        const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
        const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        newDates = getMonthStartEnd(year, lastMonth);
        break;
      case 'current_quarter':
        newDates = getCurrentQuarterStartEnd();
        break;
      case 'current_year':
        newDates = getCurrentYearStartEnd();
        break;
      case 'custom':
        // Keep the current dates for custom
        newDates = { startDate: filters.startDate, endDate: filters.endDate };
        break;
      default:
        newDates = getCurrentMonthStartEnd();
    }

    setFilters({
      ...filters,
      startDate: newDates.startDate,
      endDate: newDates.endDate
    });
  };

  const handleFilterDateSelection = (update) => {
    const [start, end] = update;

    const formatLocal = (date) => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setFilters({
      ...filters,
      startDate: formatLocal(start),
      endDate: formatLocal(end)
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const applyFilters = () => {
    // Reset to page 1 when applying new filters
    const newParams = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      page: 1,
      limit: filters.limit
    };

    if (filters.paymentType) newParams.paymentType = filters.paymentType;
    if (filters.buildingId) newParams.buildingId = filters.buildingId;
    if (filters.ownerId) newParams.ownerId = filters.ownerId;
    if (filters.tenantId) newParams.tenantId = filters.tenantId;

    setSearchParams(newParams);
    setFilterOpen(false);
  };

  const resetFilters = () => {
    const defaultDates = getCurrentMonthStartEnd();
    setFilters({
      startDate: defaultDates.startDate,
      endDate: defaultDates.endDate,
      paymentType: '',
      buildingId: '',
      ownerId: '',
      tenantId: '',
      page: 1,
      limit: 20
    });
    setSelectedPeriod('current_month');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Client-side filtering already implemented in the filteredLogs computation
  };

  const handlePageChange = (newPage) => {
    const newParams = { ...Object.fromEntries(searchParams), page: newPage };
    setSearchParams(newParams);
  };

  // Filter logs if search term is used
  const filteredLogs = data?.paymentLogs?.filter(log => {
    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    return (
      log.tenantName?.toLowerCase().includes(term) ||
      log.buildingName?.toLowerCase().includes(term) ||
      log.ownerName?.toLowerCase().includes(term) ||
      log.paymentType?.toLowerCase().includes(term) ||
      log.apartmentNumber?.toLowerCase().includes(term)
    );
  }) || [];

  // Handle Print Functionality
  const handlePrint = () => {
    const originalTitle = document.title;
    const reportTitle = `${t('payment_logs')} - ${formatDate(startDate)} ${t('to')} ${formatDate(endDate)}`;
    document.title = reportTitle;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');

    // Define CSS styles for the print window
    const printStyles = `
      @media print {
        @page { size: landscape; }
        body {
          font-family: Arial, sans-serif;
          padding: 20px;
          direction: ${isRTL ? 'rtl' : 'ltr'};
        }
        h1 {
          font-size: 18px;
          margin-bottom: 10px;
          text-align: center;
        }
        .period {
          font-size: 14px;
          text-align: center;
          margin-bottom: 20px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: ${isRTL ? 'right' : 'left'};
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .amount {
          text-align: ${isRTL ? 'left' : 'right'};
          font-weight: bold;
        }
        .footer {
          margin-top: 20px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
      }
    `;

    // Generate the HTML content for printing
    const printContent = `
      <!DOCTYPE html>
      <html lang="${i18n.language}" dir="${isRTL ? 'rtl' : 'ltr'}">
        <head>
          <meta charset="UTF-8">
          <title>${reportTitle}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <div class="period">${formatDate(startDate)} - ${formatDate(endDate)}</div>
          <table>
            <thead>
              <tr>
                <th>${t('payment_id')}</th>
                <th>${t('date')}</th>
                <th>${t('tenant')}</th>
                <th>${t('building')}</th>
                <th>${t('type')}</th>
                <th class="amount">${t('amount')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredLogs.map(payment => `
                <tr>
                  <td>${payment.paymentId}</td>
                  <td>${formatDate(payment.paymentDate)}</td>
                  <td>${payment.tenantName}</td>
                  <td>${payment.buildingName} - ${payment.apartmentNumber}</td>
                  <td>${t(payment.paymentType) || payment.paymentType}</td>
                  <td class="amount">${formatCurrency(payment.amount)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            ${t('printed_on')} ${new Date().toLocaleDateString(i18n.language)}
          </div>
        </body>
      </html>
    `;

    // Write to the new window and print
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
        document.title = originalTitle;
      };
    };
  };

  // Handle Export to Excel Functionality
  const handleExportToExcel = async () => {
    try {
      setExporting(true);

      // If no data, don't proceed
      if (!filteredLogs || filteredLogs.length === 0) {
        setExporting(false);
        return;
      }

      // Prepare data for export
      const exportData = filteredLogs.map(payment => ({
        [t('payment_id')]: payment.paymentId,
        [t('date')]: formatDate(payment.paymentDate),
        [t('tenant')]: payment.tenantName,
        [t('building')]: `${payment.buildingName} - ${payment.apartmentNumber}`,
        [t('type')]: t(payment.paymentType) || payment.paymentType,
        [t('amount')]: payment.amount,
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Payment ID
        { wch: 15 }, // Date
        { wch: 30 }, // Tenant
        { wch: 30 }, // Building
        { wch: 15 }, // Type
        { wch: 15 }, // Amount
      ];
      ws['!cols'] = columnWidths;

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('payment_logs'));

      // Generate file name
      const fileName = `${t('payment_logs')}_${startDate}_${endDate}_${new Date().getTime()}.xlsx`;

      // Export to file
      XLSX.writeFile(wb, fileName);

    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(t('failed_to_export') || 'Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiFileChartLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            {t('payment_logs') || 'Payment Logs'}
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
            {formatDate(startDate)} {t('to') || 'to'} {formatDate(endDate)}
          </p>
        </div>

        <div className={`flex items-center ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
              } border flex items-center`}
          >
            <RiFilter2Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('filter') || 'Filter'}
          </button>

          <Link
            to="/admin/reports"
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } flex items-center`}
          >
            <BackArrow className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('back') || 'Back'}
          </Link>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className={`p-4 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                <RiSearchLine className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
              </div>
              <input
                type="text"
                className={`block w-full p-2.5 ${isRTL ? 'pr-10' : 'pl-10'} rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
                placeholder={t('search_payment_logs_placeholder') || "Search payment logs..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('showing_entries', { showing: filteredLogs.length, total: data?.totalCount || 0 }) ||
                `Showing ${filteredLogs.length} of ${data?.totalCount || 0} entries`}
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {filterOpen && (
        <div className={`p-4 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('filter_payments') || 'Filter Payments'}
            </h3>
            <button
              onClick={() => setFilterOpen(false)}
              className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-200 text-gray-500'
                }`}
            >
              <RiCloseLine size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('time_period') || 'Time Period'}
              </label>
              <select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                {TIME_PERIOD_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.value) || option.label}
                  </option>
                ))}
              </select>
            </div>

            {selectedPeriod === 'custom' && (
              <div className="md:col-span-1">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('select_date_range') || 'Select Date Range'}
                </label>
                <div className="relative">
                  <DatePicker
                    selectsRange={true}
                    startDate={filters.startDate ? new Date(filters.startDate) : null}
                    endDate={filters.endDate ? new Date(filters.endDate) : null}
                    onChange={handleFilterDateSelection}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select start and end date"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    portalId="root-portal"
                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                  <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none`} />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('payment_type') || 'Payment Type'}
              </label>
              <select
                name="paymentType"
                value={filters.paymentType}
                onChange={handleFilterChange}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="">{t('all_types') || 'All Types'}</option>
                <option value="rent">{t('rent') || 'Rent'}</option>
                <option value="service">{t('service') || 'Service'}</option>
                <option value="commission">{t('commission') || 'Commission'}</option>
                <option value="insurance">{t('insurance') || 'Insurance'}</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('entries_per_page') || 'Entries Per Page'}
              </label>
              <select
                name="limit"
                value={filters.limit}
                onChange={handleFilterChange}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
            <button
              onClick={resetFilters}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {t('reset') || 'Reset'}
            </button>

            <button
              onClick={applyFilters}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                } flex items-center`}
            >
              <RiFilter2Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('apply_filter') || 'Apply Filter'}
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
          } border flex items-center`}>
          <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      {/* Payment Logs Table */}
      {!loading && !error && data && (
        <>
          <div className={`rounded-xl shadow overflow-hidden ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
            }`} ref={printRef}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${isDarkMode ? 'bg-zinc-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left">{t('payment_id') || 'Payment ID'}</th>
                    <th scope="col" className="px-4 py-3 text-left">{t('date') || 'Date'}</th>
                    <th scope="col" className="px-4 py-3 text-left">{t('tenant') || 'Tenant'}</th>
                    <th scope="col" className="px-4 py-3 text-left">{t('building') || 'Building'}</th>
                    <th scope="col" className="px-4 py-3 text-left">{t('type') || 'Type'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('amount') || 'Amount'}</th>
                    <th scope="col" className="px-4 py-3 text-center">{t('actions') || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 text-gray-300' : 'divide-gray-200 text-gray-700'}`}>
                  {filteredLogs.length > 0 ? (
                    filteredLogs.map(payment => (
                      <tr
                        key={payment.paymentId}
                        className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                          } transition-colors duration-150`}
                      >
                        <td className="px-4 py-3">{payment.paymentId}</td>
                        <td className="px-4 py-3">{formatDate(payment.paymentDate)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-gray-500`} />
                            {payment.tenantName}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <RiBuildingLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-gray-500`} />
                            {payment.buildingName} - {payment.apartmentNumber}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${payment.paymentType === 'rent'
                            ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-700'
                            : payment.paymentType === 'service'
                              ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'
                              : isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700'
                            }`}>
                            {t(payment.paymentType) || payment.paymentType}
                          </span>
                        </td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'} font-medium`}>
                          {formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className={`flex items-center justify-center ${isRTL ? 'space-x-2 space-x-reverse' : 'space-x-2'}`}>
                            <button
                              onClick={() => window.open(`/admin/reports/print-receipt/${payment.paymentId}`, '_blank')}
                              className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                                }`}
                              title={t('print_receipt') || "Print Receipt"}
                            >
                              <RiPrinterLine />
                            </button>

                            <Link
                              to={`/admin/payments/${payment.paymentId}`}
                              className={`p-1.5 rounded-lg ${isDarkMode ? 'hover:bg-gray-600 text-red-400' : 'hover:bg-zinc-100 text-red-600'
                                }`}
                              title={t('view_details') || "View Details"}
                            >
                              <RiFileChartLine />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="px-4 py-6 text-center">
                        {searchTerm
                          ? t('no_logs_matching_search') || 'No payment logs matching your search criteria'
                          : t('no_logs_for_period') || 'No payment logs available for the selected time period'
                        }
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {data.totalCount > filters.limit && (
            <div className="flex justify-between items-center mt-4">
              <div className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                {t('showing_range', { start: (page - 1) * filters.limit + 1, end: Math.min(page * filters.limit, data.totalCount), total: data.totalCount }) ||
                  `Showing ${(page - 1) * filters.limit + 1} to ${Math.min(page * filters.limit, data.totalCount)} of ${data.totalCount} entries`}
              </div>

              <div className={`flex ${isRTL ? 'space-x-2 space-x-reverse' : 'space-x-2'}`}>
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className={`px-3 py-1 rounded-lg ${page === 1
                    ? isDarkMode ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-zinc-400 cursor-not-allowed'
                    : isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {t('previous') || 'Previous'}
                </button>

                {Array.from({ length: Math.ceil(data.totalCount / filters.limit) }).map((_, i) => {
                  // Show a limited number of pages
                  if (
                    i === 0 || // First page
                    i === Math.ceil(data.totalCount / filters.limit) - 1 || // Last page
                    (i >= page - 2 && i <= page + 2) // Pages around current page
                  ) {
                    return (
                      <button
                        key={i}
                        onClick={() => handlePageChange(i + 1)}
                        className={`px-3 py-1 rounded-lg ${page === i + 1
                          ? isDarkMode ? 'bg-red-600 text-white' : 'bg-zinc-500 text-white'
                          : isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                      >
                        {i + 1}
                      </button>
                    );
                  } else if (
                    (i === 1 && page > 3) || // Show ellipsis after first page if current page is away
                    (i === Math.ceil(data.totalCount / filters.limit) - 2 && page < Math.ceil(data.totalCount / filters.limit) - 3) // Show ellipsis before last page if current page is away
                  ) {
                    return (
                      <span
                        key={i}
                        className={`px-3 py-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}
                      >
                        ...
                      </span>
                    );
                  }

                  return null;
                })}

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === Math.ceil(data.totalCount / filters.limit)}
                  className={`px-3 py-1 rounded-lg ${page === Math.ceil(data.totalCount / filters.limit)
                    ? isDarkMode ? 'bg-zinc-800 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-zinc-400 cursor-not-allowed'
                    : isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                >
                  {t('next') || 'Next'}
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
            <button
              onClick={handlePrint}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } flex items-center`}
              disabled={filteredLogs.length === 0}
            >
              <RiPrinterLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('print_report') || 'Print Report'}
            </button>

            <button
              onClick={handleExportToExcel}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                } flex items-center`}
              disabled={exporting || filteredLogs.length === 0}
            >
              {exporting ? (
                <>
                  <div className={`h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  {t('exporting') || 'Exporting...'}
                </>
              ) : (
                <>
                  <RiDownloadLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('export_to_excel') || 'Export to Excel'}
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentLogsReport;